import { z } from 'zod';

/**
 * 17a (parent §2 "Agent definition + admitted deployment"): the executor contract of an admitted agent.
 * Documented boundary only: today every agent runs in-process through the `ReasoningRuntime` port. A remote or
 * independently hosted agent must satisfy this contract; it proposes governed actions back to Falak or acts
 * through a governed connector, and never performs side effects outside the tool gateway.
 */

export const ExecutorKind = z.enum(['in_process', 'remote']).meta({ id: 'ExecutorKind' });
export type ExecutorKind = z.infer<typeof ExecutorKind>;

/** Who performs side effects the agent causes. `none` is for read-only agents. */
export const SideEffectOwnership = z
  .enum(['none', 'governed_actions', 'governed_connector'])
  .meta({ id: 'SideEffectOwnership' });
export type SideEffectOwnership = z.infer<typeof SideEffectOwnership>;

export const AgentExecutorContract = z
  .object({
    kind: ExecutorKind,
    /** Remote endpoint reference (resolved from deployment config, never a literal URL with credentials). */
    endpointRef: z.string().min(1).optional(),
    /** Input and output schema refs validated at the boundary on both sides. */
    inputSchemaRef: z.string().min(1),
    outputSchemaRef: z.string().min(1),
    /** Workload identity the executor authenticates as (mapped by the identity profile). */
    identity: z.string().min(1),
    /** Cancellation: the platform sends cancel and waits up to `cancelGraceSeconds` before abandoning. */
    cancel: z.object({
      supported: z.boolean(),
      cancelGraceSeconds: z.number().int().nonnegative().default(30),
    }),
    timeoutSeconds: z.number().int().positive(),
    /** Progress reports (heartbeats) the executor sends; missing heartbeats past the interval fail the episode. */
    progress: z.object({ heartbeatSeconds: z.number().int().positive().optional() }).prefault({}),
    /** Error classes the executor may return; anything else maps to `internal`. */
    errors: z.array(z.enum(['validation', 'timeout', 'downstream', 'policy', 'cancelled'])).default([]),
    sideEffects: SideEffectOwnership,
    /** For `governed_connector`: the connector (tool) names the executor may call through the gateway. */
    connectors: z.array(z.string().min(1)).default([]),
  })
  .superRefine((c, ctx) => {
    if (c.kind === 'remote' && c.endpointRef === undefined) {
      ctx.addIssue({ code: 'custom', message: 'remote executors need endpointRef' });
    }
    if (c.sideEffects === 'governed_connector' && c.connectors.length === 0) {
      ctx.addIssue({ code: 'custom', message: 'governed_connector needs at least one connector' });
    }
  })
  .meta({ id: 'AgentExecutorContract' });
export type AgentExecutorContract = z.infer<typeof AgentExecutorContract>;
